import { memo, useCallback, useEffect, useMemo, useState } from 'react';

import { type AirActionTrackingLocation } from '@air/analytics';
import { ShortUrlObjectType } from '@air/api/types';
import { Modal } from '@air/primitive-modal';
import { useToasts } from '@air/provider-toast';

import { NoPermissionToCreateShareLink } from '~/components/NoPermissionToCreateShareLink';
import { SharePrivateBoardTabs } from '~/components/SharePrivateBoardModal/SharePrivateBoardTabs';
import { ReadonlySharePrivateLink } from '~/components/SharePrivateContentModal/ReadonlySharePrivateLink';
import { ShareLinkTeamInviteNudgePanel } from '~/components/SharePrivateContentModal/ShareLinkTeamInviteNudgePanel';
import { ShareModalHeader } from '~/components/SharePrivateContentModal/ShareModalHeader';
import { SharePrivateContentSpinner } from '~/components/SharePrivateContentModal/SharePrivateContentSpinner';
import { SHARE_PRIVATE_CONTENT_MODAL } from '~/constants/testIDs';
import { useShowTurnOnShareLinkNotifications } from '~/hooks/turnOnEmailShareNotificationsToast/useShowTurnOnShareLinkNotifications';
import { useBoardPermissions } from '~/hooks/useBoardPermissions';
import { useShowShareLinkTeamInviteNudgeForModal } from '~/hooks/useShowShareLinkTeamInviteNudgeForModal';
import { useShowSubscriptionExpiredModal } from '~/hooks/useShowSubscriptionExpiredModal';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { useGetExistingShortIdInformation } from '~/swr-hooks/shortUrl/useGetExistingShortIdInformation';
import { useGetShortIdForBoard } from '~/swr-hooks/shortUrl/useGetShortIdForBoard';
import { canCreateShortUrl, canEditShortUrl, canViewShortUrl } from '~/utils/permissions/shortUrlPermissions';

export type SharePrivateBoardModalProps = AirModalProps<{
  boardId: string;
  trackLocation: AirActionTrackingLocation;
}>;

export const SharePrivateBoardModal = memo(({ boardId, onClose, trackLocation }: SharePrivateBoardModalProps) => {
  const { currentWorkspace } = useCurrentWorkspace();
  const { showingSubscriptionExpiredModal } = useShowSubscriptionExpiredModal({ onClose });
  const { boardPermissions } = useBoardPermissions({
    boardId,
  });

  const { showToast } = useToasts();
  const [hasCreatedShareLinkInThisSession, setHasCreatedShareLinkInThisSession] = useState(false);

  const { showTurnOnShareLinkNotifications } = useShowTurnOnShareLinkNotifications();
  const canEdit = canEditShortUrl(boardPermissions);
  const canCreate = canCreateShortUrl(boardPermissions);

  const { data: shortIdInfo, isLoading: isInitialLoading } = useGetExistingShortIdInformation({
    id: boardId,
    type: ShortUrlObjectType.board,
    boardContext: undefined,
    canView: canViewShortUrl(boardPermissions),
  });

  const {
    mutate: createShortIdForBoard,
    isPending: isCreatingShortIdForBoard,
    isError: isErrorCreatingShortIdForBoard,
  } = useGetShortIdForBoard();

  const hasShortId = !!shortIdInfo;
  const handleShareLinkCreatedFallback = useCallback(() => {
    showTurnOnShareLinkNotifications({
      onManageClick: onClose,
    });
  }, [onClose, showTurnOnShareLinkNotifications]);
  const { isShareLinkTeamInviteNudgeVisible, openShareLinkTeamInviteNudgeModal, workspaceName } =
    useShowShareLinkTeamInviteNudgeForModal({
      isShareLinkCreated: hasCreatedShareLinkInThisSession,
      isShareLinkReady: hasShortId,
      location: 'board_share_link',
      onShareLinkCreatedFallback: handleShareLinkCreatedFallback,
    });

  useEffect(() => {
    const workspaceId = currentWorkspace?.id;

    if (!workspaceId) {
      return;
    }
    if (
      !isInitialLoading &&
      !hasShortId &&
      canCreate &&
      !isCreatingShortIdForBoard &&
      !isErrorCreatingShortIdForBoard
    ) {
      createShortIdForBoard(
        { workspaceId, id: boardId },
        {
          onSuccess: () => {
            setHasCreatedShareLinkInThisSession(true);
          },
          onError: () => {
            showToast('Something went wrong. Please try again later.', {
              color: 'red',
            });
          },
        },
      );
    }
  }, [
    currentWorkspace?.id,
    boardId,
    canCreate,
    createShortIdForBoard,
    isInitialLoading,
    isCreatingShortIdForBoard,
    isErrorCreatingShortIdForBoard,
    showToast,
    hasShortId,
  ]);

  const ModalContent = useMemo(() => {
    if (!boardPermissions) {
      return <SharePrivateContentSpinner />;
    } else if (!shortIdInfo) {
      if (canCreate) {
        return <SharePrivateContentSpinner />;
      } else {
        return <NoPermissionToCreateShareLink />;
      }
    } else {
      if (!canEdit) {
        return (
          <ReadonlySharePrivateLink
            objectType={ShortUrlObjectType.board}
            shareId={shortIdInfo.id}
            objectId={boardId}
            trackLocation={trackLocation}
          />
        );
      } else {
        return (
          <SharePrivateBoardTabs
            boardId={boardId}
            trackLocation={trackLocation}
            shortIdInfo={shortIdInfo}
            userPermissions={boardPermissions}
          />
        );
      }
    }
  }, [boardId, canCreate, canEdit, boardPermissions, shortIdInfo, trackLocation]);

  if (showingSubscriptionExpiredModal) {
    return null;
  }

  return (
    <Modal
      className="bg-transparent p-0 focus-visible:ring-0"
      data-testid={SHARE_PRIVATE_CONTENT_MODAL}
      dangerouslyBypassFocusLock
      isOpen
      onDismiss={onClose}
    >
      <div className="rounded-6 bg-surface-1 p-6">
        <ShareModalHeader onClose={onClose} title="Share link to board" />
        {ModalContent}
      </div>
      {isShareLinkTeamInviteNudgeVisible && (
        <ShareLinkTeamInviteNudgePanel
          onInviteClick={openShareLinkTeamInviteNudgeModal}
          workspaceName={workspaceName}
        />
      )}
    </Modal>
  );
});

SharePrivateBoardModal.displayName = 'SharePrivateBoardModal';
